import React, { ForwardedRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SelectOption } from '@shared/ui/Select/model/types';

type TValue = string | number;

export const useSelect = ({
    options,
    filteredOptions,
    ref,
    value,
    needSearch,
    onChange,
    setFilter,
    highlightedIndex,
    setHighlightedIndex,
    isDisabled,
    isSmartPosition,
    dropdownMaxHeight,
}: {
    options: SelectOption[];
    filteredOptions: SelectOption[];
    ref: ForwardedRef<HTMLDivElement>;
    value: TValue;
    needSearch: boolean;
    onChange: (val: TValue) => void;
    setFilter: React.Dispatch<React.SetStateAction<string>>;
    highlightedIndex: number;
    setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
    isDisabled?: boolean;
    isSmartPosition?: boolean;
    dropdownMaxHeight?: number;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();

    const selectRef = useRef<HTMLDivElement>(null);
    const selectInnerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const setSelectRefs = useCallback(
        (node: HTMLDivElement | null) => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            selectRef.current = node;
        },
        [ref]
    );

    const setRefs = useCallback(
        (node: HTMLDivElement | null) => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref) {
                (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
            selectInnerRef.current = node;
        },
        [ref]
    );

    const openMenu = () => setIsOpen(true);
    const closeMenu = () => setIsOpen(false);

    const selectedOption = useMemo(
        () => options?.find((o) => o.value === value) || null,
        [options, value]
    );

    useEffect(() => {
        const calcRect = () => {
            if (!isSmartPosition) {
                setDropdownStyle({});
                return;
            }

            if (!selectInnerRef.current) return;

            const rect = selectInnerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < (dropdownMaxHeight ?? 170);

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);
        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [dropdownMaxHeight, isOpen, isSmartPosition]);

    useEffect(() => {
        if (isOpen && needSearch && inputRef.current) {
            inputRef?.current?.focus();
        }
        if (!isOpen) setFilter('');
    }, [isOpen, needSearch, setFilter]);

    useEffect(() => {
        if (!isOpen) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (selectInnerRef.current && !selectInnerRef.current.contains(e.target as Node)) {
                closeMenu();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const openMenuFunc = useCallback(() => {
        if (isDisabled) return;
        if (!isOpen) {
            const idx = filteredOptions.findIndex((o) => o.value === value);
            setHighlightedIndex(idx >= 0 ? idx : 0);
            openMenu();
        } else {
            closeMenu();
        }
    }, [isOpen, filteredOptions, value, setHighlightedIndex, isDisabled]);

    const handleSelect = useCallback(
        (option: SelectOption) => {
            onChange(option.value);
            closeMenu();
        },
        [onChange]
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                const idx = filteredOptions.findIndex((o) => o.value === value);
                setHighlightedIndex(idx >= 0 ? idx : 0);
                openMenu();
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            setHighlightedIndex((prev) => {
                if (prev === null) return 0;
                return Math.min(prev + 1, filteredOptions.length - 1);
            });
        } else if (e.key === 'ArrowUp') {
            setHighlightedIndex((prev) => {
                if (prev === null) return filteredOptions.length - 1;
                return Math.max(prev - 1, 0);
            });
        } else if (
            e.key === 'Enter' &&
            highlightedIndex !== null &&
            filteredOptions[highlightedIndex]
        ) {
            handleSelect(filteredOptions[highlightedIndex]);
        } else if (e.key === 'Escape') {
            closeMenu();
        }
    };

    return {
        isOpen,
        setIsOpen,
        selectInnerRef,
        inputRef,
        setRefs,
        handleKeyDown,
        selectedOption,
        openMenu: openMenuFunc,
        closeMenu,
        handleSelect,
        t,
        setSelectRefs,
        dropdownStyle,
    };
};
